import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiIcon, TuiButton, TuiHint, TuiInput } from '@taiga-ui/core';
import { TuiChip } from '@taiga-ui/kit';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { ActiveFilterChip } from '@features/requests/request-list/types';
import { OBD_STATUS_FILTER_BUTTON_TRANSLATION_KEY } from '@core/constants';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { IconButtonComponent } from '@common/components/icon-button/icon-button.component';
import { EditDesktopTableButtonComponent } from './components';
import { OutkeepSettings, RequestTableColumn } from '@core/types';
import { map } from 'rxjs/operators';
import { SvgIconComponent } from '@common/components/svg-icon/svg-icon.component';

@Component({
  selector: 'x-request-list-filters-buttons',
  imports: [
    SvgIconComponent,
    CommonModule,
    TuiHint,
    TuiButton,
    TuiInput,
    FormsModule,
    TranslatePipe,
    TuiChip,
    TuiIcon,
    IconButtonComponent,
    EditDesktopTableButtonComponent,
  ],
  templateUrl: './request-list-filters-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestListFiltersButtonsComponent {
  $filterIsActive = input<boolean>(false);
  $showAssignedToMeFilter = input<boolean>(false);
  $assignedToMeFilterIsActive = input<boolean>(false);
  // активность фильтров кнопок «Исполнитель» / «Заказчик»
  $contractorFilterIsActive = input<boolean>(false);
  $ownerFilterIsActive = input<boolean>(false);
  $hideCompletedRequestsFilterIsActive = input<boolean>(false);
  $inWorkRequestsFilterIsActive = input<boolean>(false);
  $todayRequestsFilterIsActive = input<boolean>(false);
  $tomorrowRequestsFilterIsActive = input<boolean>(false);

  $viewObserver = input<Subject<never>>();
  $isMobile = input<boolean>(false);
  $insideSheet = input<boolean>(false);
  $desktopTableColumns = input<RequestTableColumn[]>([], {
    alias: 'desktopTableColumns',
  });
  $clearFilters = output<void>({ alias: 'clearFiltersOutput' });
  $searchRequests = output<void>({ alias: 'searchRequestsOutput' });
  $assignedToMe = output<boolean>({ alias: 'assignedToMeOutput' });
  $hideCompleted = output<boolean>({ alias: 'hideCompletedOutput' });
  $showInWork = output<boolean>({ alias: 'showInWork' });
  $showToday = output<boolean>({ alias: 'showToday' });
  $showTomorrow = output<boolean>({ alias: 'showTomorrow' });
  $tableSettingsChanged = output<OutkeepSettings>({
    alias: 'tableSettingsChanged',
  });
  $removeAdditionalFilter = output<ActiveFilterChip>({
    alias: 'removeAdditionalFilter',
  });
  $toggleMobileFiltersSheet = output<void>({
    alias: 'toggleMobileFiltersSheet',
  });

  $toggleDesktopFiltersModal = output<void>({
    alias: 'toggleDesktopFiltersModal',
  });

  // открытие модалок фильтров «Исполнитель» / «Заказчик» (обрабатывает родитель)
  $openContractorFilter = output<void>({ alias: 'openContractorFilter' });
  $openOwnerFilter = output<void>({ alias: 'openOwnerFilter' });
  $clearContractorFilter = output<void>({ alias: 'clearContractorFilter' });
  $clearOwnerFilter = output<void>({ alias: 'clearOwnerFilter' });

  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  // публичный — используется в шаблоне для показа кнопки «Заказчик» по праву
  public readonly permissionService = inject(OutkeepPermissionService);

  // стрелочные функции — чтобы работал click.call() из шаблона #filterBtn
  public readonly openContractorFilter = () =>
    this.$openContractorFilter.emit();
  public readonly openOwnerFilter = () => this.$openOwnerFilter.emit();

  public clearContractorFilter(): void {
    this.$clearContractorFilter.emit();
  }

  public clearOwnerFilter(): void {
    this.$clearOwnerFilter.emit();
  }

  public commonFiltersButtons = [
    {
      title: 'filters_buttons.hide_completed',
      active: this.$hideCompletedRequestsFilterIsActive,
      click: this.hideCompleted.bind(this),
    },
    {
      title: 'filters_buttons.in_work',
      active: this.$inWorkRequestsFilterIsActive,
      click: this.showInWork.bind(this),
    },
    {
      title: 'filters_buttons.today',
      active: this.$todayRequestsFilterIsActive,
      click: this.showToday.bind(this),
    },
    {
      title: 'filters_buttons.tomorrow',
      active: this.$tomorrowRequestsFilterIsActive,
      click: this.showTomorrow.bind(this),
    },
  ];

  public additionalFiltersButtons$: Observable<ActiveFilterChip[]> =
    this.requestFiltersService.additionalFiltersButtons$;
  public mobileAdditionalFiltersButtons$: Observable<ActiveFilterChip[]> =
    this.additionalFiltersButtons$.pipe(
      map(filters => this.sortAdditionalFiltersForMobile(filters)),
    );

  clearFilters(): void {
    if (this.$assignedToMeFilterIsActive()) {
      this.$assignedToMe.emit(false);
    }
    if (this.$hideCompletedRequestsFilterIsActive()) {
      this.$hideCompleted.emit(false);
    }
    if (this.$inWorkRequestsFilterIsActive()) {
      this.$showInWork.emit(false);
    }
    if (this.$todayRequestsFilterIsActive()) {
      this.$showToday.emit(false);
    }
    if (this.$tomorrowRequestsFilterIsActive()) {
      this.$showTomorrow.emit(false);
    }

    this.$clearFilters.emit();
  }

  searchRequests(): void {
    this.$searchRequests.emit();
    if (this.$insideSheet()) {
      this.$viewObserver()!.complete();
    }
  }

  hideCompleted(): void {
    this.$hideCompleted.emit(!this.$hideCompletedRequestsFilterIsActive());
  }

  showAssignedToMe(): void {
    this.$assignedToMe.emit(!this.$assignedToMeFilterIsActive());
  }

  showInWork(): void {
    this.$showInWork.emit(!this.$inWorkRequestsFilterIsActive());
  }

  showToday(): void {
    this.$showToday.emit(!this.$todayRequestsFilterIsActive());
  }

  showTomorrow(): void {
    this.$showTomorrow.emit(!this.$tomorrowRequestsFilterIsActive());
  }

  removeAdditionalFilter(filter: ActiveFilterChip): void {
    if (!filter.removable) {
      return;
    }

    this.$removeAdditionalFilter.emit(filter);
  }

  @pure
  public sortAdditionalFiltersForMobile(
    filters: ActiveFilterChip[],
  ): ActiveFilterChip[] {
    return [...filters].sort((left, right) => {
      if (!!left.pinnedToEnd === !!right.pinnedToEnd) {
        return 0;
      }

      return left.pinnedToEnd ? 1 : -1;
    });
  }

  @pure
  public getFilterDisplayValue(filter: { title: string; value: any }): string {
    if (filter.title === OBD_STATUS_FILTER_BUTTON_TRANSLATION_KEY) {
      return this.outkeepTranslateService.instant(
        'obd_statuses.' + filter.value,
      );
    }
    if (filter.value === true || filter.value === false) {
      return this.outkeepTranslateService.instant(
        'filters_buttons.' + filter.value,
      );
    }

    return filter.value;
  }
}
